/**
 * API client — JWT with automatic refresh, one fetch wrapper for the whole app.
 * 403 subscription_required routes to the paywall; 401 retries once after a
 * token refresh then routes to login. Tokens live in memory + sessionStorage
 * (never localStorage on shared machines is a user choice; "remember me"
 * upgrades to localStorage).
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
const store = () =>
  localStorage.getItem('vault_refresh') ? localStorage : sessionStorage;

export const tokens = {
  get access() { return store().getItem('vault_access') || ''; },
  get refresh() { return store().getItem('vault_refresh') || ''; },
  set({ access, refresh }, remember) {
    const s = remember === undefined ? store() : (remember ? localStorage : sessionStorage);
    if (access) s.setItem('vault_access', access);
    if (refresh) s.setItem('vault_refresh', refresh);
  },
  clear() {
    [localStorage, sessionStorage].forEach((s) => {
      s.removeItem('vault_access');
      s.removeItem('vault_refresh');
    });
  },
};

// Single-flight refresh. The API rotates refresh tokens and blacklists the old
// one on every refresh (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION). When
// the access token expires, the app fires several calls at once and they ALL
// 401 together — without coalescing, each would POST the same refresh token, the
// first would rotate+blacklist it, and the losers would 401 and wipe the good
// new tokens. So concurrent callers await ONE in-flight refresh.
let refreshing = null;
async function refreshAccess() {
  if (refreshing) return refreshing;   // a refresh is already in flight — join it
  refreshing = (async () => {
    const r = await fetch('/api/auth/token/refresh/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: tokens.refresh }),
    });
    if (!r.ok) throw new Error('refresh failed');
    const data = await r.json();
    tokens.set(data);
    return data.access;
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;   // allow a later, genuine re-refresh with the new token
  }
}

export async function api(path, { method = 'GET', body, form } = {}) {
  const call = async (access) => {
    const headers = access ? { Authorization: `Bearer ${access}` } : {};
    if (!form) headers['Content-Type'] = 'application/json';
    return fetch(`/api${path}`, {
      method, headers,
      body: form || (body !== undefined ? JSON.stringify(body) : undefined),
    });
  };
  let res = await call(tokens.access);
  if (res.status === 401 && tokens.refresh) {
    try {
      res = await call(await refreshAccess());
    } catch {
      // Dead session (e.g. after a server reset): clear the stale tokens and
      // let the CALLER decide — the app sets me=null and the visitor simply
      // sees the home page with Sign in / Start free. Never hard-redirect
      // from inside the HTTP client.
      tokens.clear();
      throw new Error('Session expired');
    }
  }
  if (res.status === 403) {
    const data = await res.clone().json().catch(() => ({}));
    if ((data.detail || '').toLowerCase().includes('trial')) {
      window.location.assign('/paywall');
      throw new Error(data.detail);
    }
  }
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.detail || `HTTP ${res.status}`), { data, status: res.status });
  // An AI endpoint that could not run answers 200 with a flag. Tell the user
  // once, app-wide, why nothing came back and where to change it.
  const blocked = aiBlockedKind(path, data);
  if (blocked) {
    window.dispatchEvent(new CustomEvent('vault:ai-blocked', { detail: { kind: blocked } }));
  }
  return data;
}

// /auth/me and the consent endpoint REPORT the permission (ai_consent_required
// is simply true until the user allows AI) — they are not an AI call that was
// blocked, so they must never raise the notice. Without this, every page load
// for an account that has not allowed AI showed "AI is not allowed" out of
// nowhere.
const AI_STATE_PATHS = ['/auth/me/', '/ai/consent/'];

/** 'consent' | 'off' | null — whether this reply is an AI call the gate
 * stopped. Only the gate's own 200 payloads count (apps/ai_service/gate.py). */
export function aiBlockedKind(path, data) {
  if (!data || typeof data !== 'object') return null;
  if (AI_STATE_PATHS.some((p) => String(path || '').split('?')[0].startsWith(p))) return null;
  if (data.ai_off === true) return 'off';
  if (data.ai_consent_required === true) return 'consent';
  return null;
}

/** Download an authenticated file (CSV export) via blob. */
export async function download(path, filename) {
  const res = await fetch(`/api${path}`, {
    headers: { Authorization: `Bearer ${tokens.access}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Fetch an authenticated binary (a receipt image or PDF) and hand back a
 * blob: object URL plus its content-type. Nginx denies /media/ outright, so a
 * receipt only reaches the browser through its owner-scoped API action; an
 * <img> can't carry the JWT, so we pull the bytes here and the caller assigns
 * the returned url to src (and revokes it when the viewer closes).
 */
export async function blobUrl(path) {
  const call = (access) =>
    fetch(`/api${path}`, { headers: access ? { Authorization: `Bearer ${access}` } : {} });
  let res = await call(tokens.access);
  if (res.status === 401 && tokens.refresh) res = await call(await refreshAccess());
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type || '' };
}
